import {AuthForm, AuthHeading, AuthWrapper} from '@/components/ui/auth-atoms/AuthAtoms'
import i18n, {useTranslation} from '@/translations/i18n'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {Button} from '@/components/ui/button/Button'
import {LoginErrorMessage} from '@/features/auth/components/login-error-message/LoginErrorMessage'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputText} from '@/components/commons/input-text/InputText'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {useState} from 'react'
import {routes} from '@/utilities/constants/routes'
import {useLogin} from '@/features/auth/services/useLogin'
import {useAuthStore} from '@/features/auth/store/store'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Helmet} from 'react-helmet'

export const LoginValidationSchema = z.object({
    email: z.string().email({
        message: i18n.t('errors:email_error')
    }),
    password: z.string().min(1, {
        message: i18n.t('errors:required')
    })
})
export type LoginValidationSchema = z.infer<typeof LoginValidationSchema>

export const Login = () => {
    const {t} = useTranslation()
    const setAuthData = useAuthStore(state => state.setAuthData)
    const [isLoginErrorMessageVisible, setIsLoginErrorMessageVisible] = useState(false)
    const form = useForm<LoginValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(LoginValidationSchema)
    })

    //login mutation
    const {mutate: login, isPending} = useLogin({
        onError: () => setIsLoginErrorMessageVisible(true),
        onSuccess: data => setAuthData(data)
    })

    const handleSubmit = form.handleSubmit(formValues => login(formValues))

    return (
        <AuthWrapper>
            <Helmet title={t('login:meta_title')} />
            <AuthForm fullWidth align="center" justify="center" gap={8} onSubmit={handleSubmit}>
                <AuthHeading showLogo title={t('login:title')} subtitle={t('login:subtitle')} />
                <Flexbox direction="column" gap={4} fullWidth>
                    <InputText
                        type="email"
                        label={t('commons:email')}
                        placeholder={t('commons:email_placeholder')}
                        errorMessage={form.formState.errors.email?.message}
                        {...form.register('email')}
                    />
                    <Flexbox direction="column" gap={2} align="end" fullWidth>
                        <InputText
                            type="password"
                            label={t('commons:password')}
                            placeholder={'••••••••••'}
                            errorMessage={form.formState.errors.password?.message}
                            visibilityToggle
                            {...form.register('password')}
                        />
                        <ButtonLink
                            disabled={isPending}
                            variant="tertiary"
                            shape="text"
                            to={routes.FORGOT_PASSWORD.path}
                        >
                            {t('commons:forgot_password')}
                        </ButtonLink>
                    </Flexbox>
                </Flexbox>
                <Button type="submit" fullWidth disabled={isPending} variant="primaryNeutral">
                    {t('commons:login')}
                    {isPending && <Spinner size={16} />}
                </Button>
                {isLoginErrorMessageVisible && (
                    <LoginErrorMessage hideMessage={() => setIsLoginErrorMessageVisible(false)} />
                )}
            </AuthForm>
        </AuthWrapper>
    )
}

Login.displayName = 'Login'
